(() => {
  if (customElements.get('fs-live-size-filter')) return;
  const preferences = new Map();
  const normalize = (value) => {
    if (window.FSMySize) return window.FSMySize.normalize(value);
    const key = String(value || '').trim().toLowerCase().replace(/[\s_-]+/g, '');
    return ({ extrasmall: 'xs', small: 's', medium: 'm', large: 'l', extralarge: 'xl', xxlarge: '2xl', xxl: '2xl', xxxlarge: '3xl', xxxl: '3xl' })[key] || key;
  };
  const format = (text, values) => text.replace(/\[(\w+)\]/g, (_, key) => values[key] ?? '');
  const keyForURL = (value, pageParam = 'page') => {
    const url = new URL(value, location.origin);
    url.searchParams.delete(pageParam);
    url.searchParams.delete('section_id');
    url.hash = '';
    url.searchParams.sort();
    return url.href;
  };
  window.addEventListener('popstate', (event) => {
    preferences.forEach((_, key) => preferences.set(key, event.state?.fsLiveSizes?.[key] || ''));
  });
  class FsLiveSizeFilter extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.container = this.closest('#ProductGridContainer');
      if (!this.container) return;
      this.original = this.container.querySelector('.collection');
      if (!this.original) return;
      this.originalHTML = this.original.innerHTML;
      this.originalPage = Number(this.dataset.page);
      this.status = this.querySelector('[role="status"]');
      this.retry = this.querySelector('[data-fs-live-size-retry]');
      this.preferenceKey = `${location.pathname}|${this.dataset.collection}`;
      this.selected = window.FSMySize?.get() ?? (preferences.get(this.preferenceKey) || '');
      this.historyURL = location.href;
      this.sequence = 0;
      this.onClick = (event) => {
        const button = event.target.closest('button');
        if (!button || !this.contains(button)) return;
        if (button.hasAttribute('data-fs-live-size-retry')) this.apply(this.selected);
        else if (button.hasAttribute('data-size')) this.apply(button.dataset.size);
      };
      this.onFacetInput = (event) => {
        const selector = event.type === 'click' ? 'facet-remove, .js-facet-remove' : 'facet-filters-form';
        if (!event.target.closest(selector)) return;
        this.cancel();
        this.optionsController?.abort();
        this.restore();
        const clear = event.target.closest('.active-facets__button-remove, .mobile-facets__footer .mobile-facets__clear');
        if (clear) { window.FSMySize?.set('', {source:this}); this.selected = ''; preferences.set(this.preferenceKey, ''); this.updateButtons(); }
        this.status.textContent = this.selected ? this.dataset.labelLoading : '';
      };
      this.onHistory = () => {
        if (this.historyURL === location.href && this.selected === (window.FSMySize?.get() ?? (preferences.get(this.preferenceKey) || ''))) return;
        this.historyURL = location.href;
        this.cancel(); this.optionsController?.abort(); this.restore();
        queueMicrotask(() => { if (this.isConnected) this.apply(window.FSMySize?.get() ?? (preferences.get(this.preferenceKey) || '')); });
      };
      window.addEventListener('popstate', this.onHistory);
      this.addEventListener('click', this.onClick);
      document.addEventListener('input', this.onFacetInput, true);
      document.addEventListener('click', this.onFacetInput, true);
      try {
        const records = this.readRecords(this.original);
        this.addSizes(records);
        this.hidden = !records.some(({ meta }) => meta.hasSize) && !this.selected;
      } catch (error) {
        this.hidden = true;
      }
      if (Number(this.dataset.pages) > 1 && !this.selected) this.discoverSizes();
      this.updateButtons();
      window.FSMySize?.collectionReady(this);
      if (this.selected) queueMicrotask(() => this.isConnected && this.apply(this.selected));
    }
    disconnectedCallback() {
      window.FSMySize?.collectionDisconnected(this);
      this.cancel();
      this.optionsController?.abort();
      window.removeEventListener('popstate', this.onHistory);
      this.removeEventListener('click', this.onClick);
      document.removeEventListener('input', this.onFacetInput, true);
      document.removeEventListener('click', this.onFacetInput, true);
      this.initialized = false;
    }
    async discoverSizes() {
      this.optionsController = new AbortController();
      const signal = this.optionsController.signal;
      const baseURL = keyForURL(location.href, this.dataset.pageParam || 'page');
      try {
        const { records } = await this.loadAll(baseURL, signal, -1);
        if (signal.aborted || !this.isConnected || baseURL !== keyForURL(location.href, this.dataset.pageParam || 'page')) return;
        this.addSizes(records);
        window.FSMySize?.recordCollection(this, records, true);
        this.optionsComplete = true;
        this.updateButtons();
        if (records.some(({ meta }) => meta.hasSize)) this.hidden = false;
      } catch (error) {
        if (!signal.aborted && this.isConnected && !this.selected && !this.hidden) this.status.textContent = this.dataset.labelOptionsError || this.dataset.labelError;
      }
    }
    cancel() {
      this.sequence += 1;
      this.controller?.abort();
      this.removeAttribute('aria-busy');
    }
    readRecords(root) {
      return Array.from(root.querySelectorAll('#product-grid > li')).map((card) => {
        const source = card.querySelector('[data-fs-live-size-meta]');
        if (!source) throw new Error('Missing product size metadata');
        const meta = JSON.parse(source.dataset.fsLiveSizeMeta);
        if (!meta.id || !Array.isArray(meta.variants)) throw new Error('Invalid product size metadata');
        if (meta.hasSize && (meta.rendered !== meta.total || meta.variants.length !== meta.total)) {
          throw new Error('Incomplete product variant metadata');
        }
        return { card, meta };
      });
    }
    readPage(root, expectedPage) {
      const row = root.querySelector('fs-live-size-filter');
      const collection = root.querySelector('#ProductGridContainer .collection');
      if (!row || !collection || row.dataset.section !== this.dataset.section || row.dataset.collection !== this.dataset.collection) {
        throw new Error('Unexpected collection response');
      }
      const page = Number(row.dataset.page);
      const pages = Number(row.dataset.pages);
      const items = Number(row.dataset.items);
      if (page !== expectedPage || !Number.isInteger(pages) || pages < 0 || pages > 100 || !Number.isInteger(items) || items < 0 || items !== Number(row.dataset.productsCount)) {
        throw new Error('Incomplete collection pagination');
      }
      return { page, pages, items, collection, records: this.readRecords(collection) };
    }
    async fetchPage(baseURL, page, signal) {
      const url = new URL(baseURL);
      if (url.origin !== location.origin || url.pathname !== location.pathname) throw new Error('Invalid collection URL');
      url.searchParams.set('section_id', this.dataset.section);
      url.searchParams.delete('preview_theme_id');
      const preview = this.dataset.previewThemeId;
      if (preview && /^\d+$/.test(preview)) url.searchParams.set('preview_theme_id', preview);
      url.searchParams.set(this.dataset.pageParam || 'page', String(page));
      const timeout = new AbortController();
      const abort = () => timeout.abort();
      signal.addEventListener('abort', abort, { once: true });
      if (signal.aborted) timeout.abort();
      const timer = setTimeout(abort, 20000);
      try {
        const response = await fetch(url.href, { signal: timeout.signal, credentials: 'same-origin', cache: 'no-store' });
        if (!response.ok || (response.url && new URL(response.url).origin !== location.origin)) throw new Error('Collection request failed');
        return this.readPage(new DOMParser().parseFromString(await response.text(), 'text/html'), page);
      } finally {
        clearTimeout(timer);
        signal.removeEventListener('abort', abort);
      }
    }
    async loadAll(baseURL, signal, sequence) {
      const first = await this.fetchPage(baseURL, 1, signal);
      const pages = [first];
      let next = 2;
      let complete = 1;
      const worker = async () => {
        while (next <= first.pages) {
          const page = next++;
          const result = await this.fetchPage(baseURL, page, signal);
          if (result.pages !== first.pages || result.items !== first.items) throw new Error('Collection changed during loading');
          pages[page - 1] = result;
          complete += 1;
          if (sequence === this.sequence && this.isConnected) this.status.textContent = `${this.dataset.labelLoading} ${complete}/${first.pages}`;
        }
      };
      await Promise.all(Array.from({ length: Math.min(3, Math.max(0, first.pages - 1)) }, worker));
      if (signal.aborted) throw new DOMException('Cancelled', 'AbortError');
      const records = pages.flatMap((page) => page.records);
      const unique = new Map();
      records.forEach((record) => unique.set(String(record.meta.id), record));
      if (unique.size !== first.items || unique.size !== records.length || pages.length !== Math.max(1, first.pages)) {
        throw new Error('Collection did not return every unique product');
      }
      return { records: [...unique.values()], first };
    }
    addSizes(records) {
      if (window.FSMySize) return window.FSMySize.collectionSizes(this, records);
      const sizes = new Map();
      records.forEach(({ meta }) => meta.variants.forEach((variant) => sizes.set(normalize(variant.size), variant.size)));
      const row = this.querySelector('.fs-live-size-filter__row');
      sizes.forEach((label, size) => {
        if (!size || Array.from(this.querySelectorAll('[data-size]')).some((button) => button.dataset.size === size)) return;
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'fs-live-size-filter__button';
        button.dataset.size = size;
        button.setAttribute('aria-pressed', 'false');
        button.textContent = ['xs', 's', 'm', 'l', 'xl', '2xl', '3xl', '4xl', '5xl'].includes(size) ? size.toUpperCase() : label;
        button.setAttribute('aria-label', label);
        row.append(button);
      });
    }
    updateButtons() {
      this.querySelectorAll('.fs-live-size-filter__row [data-size]').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.size === this.selected)));
      const clear = this.querySelector('[data-fs-live-size-clear]');
      if (clear) {
        clear.hidden = !this.selected;
        const selected = this.querySelector('.fs-live-size-filter__row [aria-pressed="true"]');
        clear.querySelector('[data-fs-live-selected-label]').textContent = selected?.getAttribute('aria-label') || selected?.textContent || this.selected.toUpperCase();
      }
    }
    restore() {
      if (this.rendered) {
        this.original.innerHTML = this.originalHTML;
        this.original.querySelectorAll('.scroll-trigger').forEach((node) => node.classList.add('scroll-trigger--cancel'));
        this.rendered = false;
      }
      if (this.originalCounts) {
        this.originalCounts.forEach(({ node, html }) => { if (node.isConnected) node.innerHTML = html; });
        this.originalCounts = null;
      }
      this.original.querySelectorAll('.fs-live-size-empty').forEach((node) => node.remove());
      this.retry.hidden = true;
    }
    resetPageURL() {
      const url = new URL(location.href);
      const pageParam = this.dataset.pageParam || 'page';
      if (!url.searchParams.has(pageParam)) return;
      url.searchParams.delete(pageParam);
      history.replaceState({ ...(history.state || {}), searchParams: url.searchParams.toString() }, '', url.href);
      this.historyURL = location.href;
    }
    renderMatches(records, first, size) {
      const matches = records.flatMap(({ card, meta }) => {
        const variant = meta.variants.find((candidate) => candidate.available === true && candidate.matched !== false && normalize(candidate.size) === size);
        return variant ? [{ card, variant }] : [];
      });
      this.originalHTML = first.collection.innerHTML;
      this.originalPage = 1;
      this.original.innerHTML = first.collection.innerHTML;
      const grid = this.original.querySelector('#product-grid');
      if (grid?.tagName === 'UL') {
        grid.replaceChildren(...matches.map(({ card, variant }) => {
          const copy = card.cloneNode(true);
          copy.querySelectorAll('a[href]').forEach((link) => {
            const url = new URL(link.getAttribute('href'), location.origin);
            if (url.origin === location.origin && url.pathname.includes('/products/')) {
              url.searchParams.delete('preview_theme_id');
              const preview = this.dataset.previewThemeId;
              if (preview && /^\d+$/.test(preview)) url.searchParams.set('preview_theme_id', preview);
              url.searchParams.delete('variant');
              link.setAttribute('href', `${url.pathname}${url.search}${url.hash}`);
            }
          });
          copy.querySelectorAll('[data-variant]').forEach(node => node.removeAttribute('data-variant'));
          copy.querySelectorAll('.scroll-trigger').forEach((node) => node.classList.add('scroll-trigger--cancel'));
          copy.classList.add('scroll-trigger--cancel');
          const note = document.createElement('p');
          note.className = 'fs-live-size-card-note';
          note.textContent = format(this.dataset.labelAvailable, { size: variant.size });
          (copy.querySelector('.card-information') || copy).append(note);
          return copy;
        }));
      }
      if (!matches.length && grid?.tagName === 'UL') {
        const empty = document.createElement('p');
        empty.className = 'fs-live-size-empty';
        empty.textContent = this.dataset.labelEmpty;
        grid.after(empty);
      }
      this.original.querySelectorAll('.pagination-wrapper').forEach((pagination) => { pagination.hidden = true; });
      this.rendered = true;
      this.resetPageURL();
      const label = this.querySelector('[data-size][aria-pressed="true"]')?.textContent || size.toUpperCase();
      this.status.textContent = format(this.dataset.labelResult, { count: matches.length, size: label, total: first.items });
      this.originalCounts = Array.from(document.querySelectorAll('#ProductCount, #ProductCountDesktop')).map((node) => ({ node, html: node.innerHTML }));
      this.originalCounts.forEach(({ node }) => { node.textContent = format(this.dataset.labelCount, { count: matches.length }); });
    }
    async apply(size) {
      size = normalize(size);
      window.FSMySize?.set(size, {source:this});
      this.cancel();
      this.optionsController?.abort();
      this.selected = size;
      preferences.set(this.preferenceKey, size);
      history.replaceState({ ...(history.state || {}), fsLiveSizes: { ...(history.state?.fsLiveSizes || {}), [this.preferenceKey]: size } }, '', location.href);
      this.historyURL = location.href;
      this.updateButtons();
      this.restore();
      this.status.textContent = '';
      if (!size) {
        if (!this.optionsComplete && Number(this.dataset.pages) > 1) this.discoverSizes();
        return;
      }
      const sequence = this.sequence;
      const baseURL = keyForURL(location.href, this.dataset.pageParam || 'page');
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.setAttribute('aria-busy', 'true');
      this.status.textContent = this.dataset.labelLoading;
      try {
        const { records, first } = await this.loadAll(baseURL, signal, sequence);
        if (signal.aborted || sequence !== this.sequence || !this.isConnected || baseURL !== keyForURL(location.href, this.dataset.pageParam || 'page')) return;
        this.addSizes(records);
        window.FSMySize?.recordCollection(this, records, true);
        this.optionsComplete = true;
        this.updateButtons();
        this.renderMatches(records, first, size);
      } catch (error) {
        if (signal.aborted || sequence !== this.sequence || !this.isConnected) return;
        this.controller.abort();
        this.restore();
        this.status.textContent = this.dataset.labelError;
        this.retry.hidden = false;
      } finally {
        if (sequence === this.sequence) this.removeAttribute('aria-busy');
      }
    }
  }
  customElements.define('fs-live-size-filter', FsLiveSizeFilter);
})();
